import { useCallback, useEffect, useState } from 'react'

/**
 * Whether the desktop rail is folded to its icon strip.
 *
 * A LAYOUT preference, so it persists — the same rule the Unterlagen table
 * follows for its columns. Somebody who folds the rail to give the document
 * matrix its 184 pixels back wants them back tomorrow as well.
 *
 * Plain `useState`, unlike the theme's module-level store: exactly one component
 * reads this (AppLayout), so there are no two copies to fall out of step.
 *
 * Read synchronously in the initialiser, not in an effect. The rail renders for
 * the first time with the bundle, so there is no server-painted frame to
 * flash — but an effect would still paint the wide rail once and then snap it
 * shut, with a width transition drawing attention to it on every page load.
 */

const STORAGE_KEY = 'acasa.sidebar.v1'

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'collapsed'
  } catch {
    // Private browsing or blocked storage — an unfolded rail is a fine default.
    return false
  }
}

export function useSidebar() {
  const [collapsed, setCollapsed] = useState(readStored)

  const toggle = useCallback(() => {
    setCollapsed((current) => {
      const next = !current
      try {
        localStorage.setItem(STORAGE_KEY, next ? 'collapsed' : 'expanded')
      } catch {
        // The fold still works for this visit; it just will not be remembered.
      }
      return next
    })
  }, [])

  /*
   * Ctrl/⌘ + B — what editors and most web apps with a rail use, so it is where
   * a hand goes. Bare modifier only: with Shift or Alt it belongs to somebody
   * else. No text field in this app gives ⌘B a meaning (there is no rich text),
   * so taking it while one is focused costs nothing.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'b') return
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return
      event.preventDefault()
      toggle()
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggle])

  return { collapsed, toggle }
}
